import { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from '../../../../styles/colors';
import { apiCreatePack, apiUpdatePack } from '../../../../api/packApi';
import { apiGetExpansions } from '../../../../api/expansionApi';
import type { Pack } from '../../../../types/models/pack';
import type { Expansion } from '../../../../types/models/expansion';

// ─── Styled ───────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const Modal = styled.div`
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  width: 100%;
  max-width: 480px;
  padding: 28px 24px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: gold;
  margin: 0 0 20px;
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: gold;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: gold;
  }

  option {
    background-color: #1a1a1a;
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
`;

const SubmitBtn = styled.button`
  flex: 1;
  padding: 10px;
  background-color: ${colors.bgColorBordeaux};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background-color: ${colors.bgColorRed}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CancelBtn = styled.button`
  padding: 10px 20px;
  background-color: transparent;
  color: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;

  &:hover { border-color: rgba(255, 255, 255, 0.4); }
`;

const ErrorText = styled.p`
  color: #dc3545;
  font-size: 13px;
  margin: 12px 0 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  pack?: Pack;
  onClose: () => void;
  onCreated: (pack: Pack) => void;
  onUpdated: (pack: Pack) => void;
}

const PackCreateModal: React.FC<Props> = ({ pack, onClose, onCreated, onUpdated }) => {
  const isEdit = !!pack;

  const [name, setName] = useState(pack?.name ?? '');
  const [expansionId, setExpansionId] = useState<number | ''>(pack?.expansion.id ?? '');
  const [cost, setCost] = useState(pack?.cost?.toString() ?? '');
  const [cardsPerPack, setCardsPerPack] = useState(pack?.cards_per_pack?.toString() ?? '');
  const [isActive, setIsActive] = useState(pack?.is_active ?? false);

  const [expansions, setExpansions] = useState<Expansion[]>([]);
  const [expansionsLoading, setExpansionsLoading] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    apiGetExpansions()
      .then((data) => {
        if (isMounted) setExpansions(Array.isArray(data) ? data : ((data as any).data ?? []));
      })
      .catch(() => {})
      .finally(() => { if (isMounted) setExpansionsLoading(false); });
    return () => { isMounted = false; };
  }, []);

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Il nome è obbligatorio.'); return; }
    if (!expansionId) { setError("Seleziona un'espansione."); return; }
    const costNum = parseInt(cost, 10);
    if (!cost || isNaN(costNum) || costNum <= 0) { setError('Il costo deve essere un intero positivo.'); return; }
    const cardsNum = parseInt(cardsPerPack, 10);
    if (!cardsPerPack || isNaN(cardsNum) || cardsNum <= 0) { setError('Le carte per pacchetto devono essere un intero positivo.'); return; }

    setError(null);
    setIsSubmitting(true);
    try {
      if (isEdit) {
        const updated = await apiUpdatePack(pack.id, {
          name: name.trim(),
          expansion_id: expansionId as number,
          cost: costNum,
          cards_per_pack: cardsNum,
          is_active: isActive,
        });
        onUpdated(updated);
      } else {
        const created = await apiCreatePack({
          name: name.trim(),
          expansion_id: expansionId as number,
          cost: costNum,
          cards_per_pack: cardsNum,
          is_active: isActive,
        });
        onCreated(created);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Operazione fallita.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{isEdit ? 'Modifica Pacchetto' : 'Nuovo Pacchetto'}</ModalTitle>

        <Field>
          <Label>Nome</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="es. Starter Pack"
          />
        </Field>

        <Field>
          <Label>Espansione</Label>
          <Select
            value={expansionId}
            onChange={(e) => setExpansionId(e.target.value ? Number(e.target.value) : '')}
            disabled={expansionsLoading}
          >
            <option value="">{expansionsLoading ? 'Caricamento...' : 'Seleziona espansione'}</option>
            {expansions.map((exp) => (
              <option key={exp.id} value={exp.id}>{exp.name}</option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>Costo (crediti)</Label>
          <Input
            type="number"
            min={1}
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            placeholder="es. 100"
          />
        </Field>

        <Field>
          <Label>Carte per pacchetto</Label>
          <Input
            type="number"
            min={1}
            value={cardsPerPack}
            onChange={(e) => setCardsPerPack(e.target.value)}
            placeholder="es. 5"
          />
        </Field>

        <Field>
          <CheckRow>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Pacchetto attivo (acquistabile dagli utenti)
          </CheckRow>
        </Field>

        {error && <ErrorText>{error}</ErrorText>}

        <BtnRow>
          <CancelBtn onClick={onClose}>Annulla</CancelBtn>
          <SubmitBtn onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Salvataggio...' : (isEdit ? 'Salva modifiche' : 'Crea pacchetto')}
          </SubmitBtn>
        </BtnRow>
      </Modal>
    </Overlay>
  );
};

export default PackCreateModal;
